import MarkupUtil from "@/utils/markupUtil";
import storage from "@/utils/storageUtil";
import api from '@/api';

const ratingOptions = [
    { name: "Positive", value: 'rt-pos' },
    { name: "Neutral", value: 'rt-soso' },
    { name: "Negative", value: 'rt-neg' }
];

const airatingOptions = [
    { name: "Positive", value: 'rt-pos-ai' },
    { name: "Neutral", value: 'rt-soso-ai' },
    { name: "Negative", value: 'rt-neg-ai' }
];

const ownershipOptions = [
    { name: "Owned", value: 'own-owned' },
    { name: "Not Owned", value: 'own-not-owned' },
    { name: "Not Me", value: 'own-resembler' }
];

const authorityOptions = [
    { name: "iAuthority™", value: 'ath-iauth' },
    { name: "Expert", value: 'ath-expert' },
    { name: "Social", value: 'ath-social' },
    { name: "Wikipedia", value: 'ath-wiki' },
    { name: "Crowd", value: 'ath-crowd' },
];


// highlight is a calculated value from the other two
function getHighlightMarkupLabels(state) {
    if (state.highlightMarkup && state.markup) {
        const classes = [...state.rating, ...state.airating, ...state.ownership, ...state.authority].map((o) => o.value);
        return MarkupUtil.markupSelectionsToLabels(classes);
    } else {
        return [];
    }
}

// setting highlight only for legacy compatibility.
function legacyStoreHighlight(state) {
    storage.set("highlight", getHighlightMarkupLabels(state).join(" "), true);
}

function getStoredSelections(key, options) {
    if (!storage.exists(key)) {
        return null;
    }

    try {
        const storedValues = JSON.parse(storage.get(key));
        if (!Array.isArray(storedValues)) {
            return null;
        }

        const selectedValues = new Set(storedValues);
        return options.filter(option => selectedValues.has(option.value));
    } catch (e) {
        return null;
    }
}

const state = () => ({
    markup: 'rating',
    showAiMarkup: true,
    highlightMarkup: true,

    rating: ratingOptions,
    airating: airatingOptions,
    ownership: ownershipOptions,
    authority: authorityOptions,

    ratingOptions,
    ownershipOptions,
    authorityOptions,

    markupEditorData: null,
    markupResultData: null, // the entire object of the resulttype such as imagecontainer

    aiAnalysisPollClientId: null,
    aiAnalysisPollUrls: [],
    aiAnalysisPollTimerId: null,

    // bumped after every persisted markup write (rating/ownership/authority);
    // ChartsView watches this to invalidate its caches and refetch.
    chartsCacheVersion: 0,
});

const getters = {
    markup: state => state.markup,
    showAiMarkup: state => state.showAiMarkup,
    highlightMarkup: state => state.highlightMarkup,

    rating: state => state.rating,
    ownership: state => state.ownership,
    authority: state => state.authority,

    ratingOptions: state => state.ratingOptions,
    ownershipOptions: state => state.ownershipOptions,
    authorityOptions: state => state.authorityOptions,

    highlightMarkupLabels: state => getHighlightMarkupLabels(state),
    markupLabels: state => [state.markup, (state.showAiMarkup ? 'airating' : ''), ...getHighlightMarkupLabels(state)],

    markupEditorData: state => state.markupEditorData,
    markupResultData: state => state.markupResultData,

    aiAnalysisPollClientId: state => state.aiAnalysisPollClientId,
    aiAnalysisPollUrls: state => state.aiAnalysisPollUrls,
    aiAnalysisPollTimerId: state => state.aiAnalysisPollTimerId,

    chartsCacheVersion: state => state.chartsCacheVersion,
};

const actions = {
    initializeStateFromStorage({ commit }) {
        // Read everything before committing because these mutations also persist
        // their values and update the legacy highlight key.
        const storedSelections = [
            ["markupRating", ratingOptions, "SET_RATING"],
            ["markupOwnership", ownershipOptions, "SET_OWNERSHIP"],
            ["markupAuthority", authorityOptions, "SET_AUTHORITY"],
        ].map(([key, options, mutation]) => [getStoredSelections(key, options), mutation]);
        const hasStoredAiMarkup = storage.exists("showAiMarkup");
        const storedAiMarkup = storage.get("showAiMarkup");
        const hasStoredHighlightMarkup = storage.exists("highlightMarkup");
        const storedHighlightMarkup = storage.get("highlightMarkup");
        const legacyHighlightMarkup = storage.legacyGetHighlightMarkup();
        const storedMarkup = storage.get("hlview");

        storedSelections.forEach(([selections, mutation]) => {
            if (selections !== null) {
                commit(mutation, selections);
            }
        });

        if (hasStoredAiMarkup) {
            commit("SET_SHOW_AI_MARKUP", storedAiMarkup === "true");
        }

        if (hasStoredHighlightMarkup) {
            const highlightMarkup = storedHighlightMarkup === "true" || (storedHighlightMarkup !== "false" && !!legacyHighlightMarkup);
            commit("SET_HIGHLIGHT_MARKUP", highlightMarkup);
            commit("SET_MARKUP", storedMarkup === "none" ? "" : storedMarkup);
        }
    },
    setMarkup({ commit }, markup) {
        commit('SET_MARKUP', markup);
    },
    setHighlightMarkup({ commit }, highlightMarkup) {
        commit('SET_HIGHLIGHT_MARKUP', highlightMarkup);
    },

    setRating({ commit }, rating) {
        commit('SET_RATING', rating);
    },
    setOwnership({ commit }, ownership) {
        commit('SET_OWNERSHIP', ownership);
    },
    setAuthority({ commit }, authority) {
        commit('SET_AUTHORITY', authority);
    },

    setMarkupEditorData({ commit }, payload) {
        commit('SET_MARKUP_EDITOR_DATA', payload);
    },
    setMarkupResultData({ commit }, payload) {
        commit('SET_MARKUP_RESULT_DATA', payload);
    },

    setShowAiMarkup({ commit }, payload) {
        commit('SET_SHOW_AI_MARKUP', payload);
    },

    // `target` ({ urlid, ts, resultType }) marks a url other than the open Markup dialog's
    // (the PAA answer dialog's source chips, DM-10271).
    updateMarkup({ commit, getters, rootGetters }, { markupType, newMarkup, ratingAction, possiblyAffectedUrlIds, target }) {
        const urlData = target || getters.markupEditorData;
        const urlid = urlData.urlid || 0;
        const ts = urlData.ts || 0;
        const clientid = rootGetters['routeParams/client'].clientid;
        const start = rootGetters['routeParams/start'];
        const end = rootGetters['routeParams/end'];
        const ctid = rootGetters['routeParams/ctid'];
        const resultset = rootGetters['routeParams/resultset'];
        const resultType = urlData.resultType;

        const callApi = {
            rating: () => api.rateURL(clientid, newMarkup, urlid, ts, ratingAction, start, end, ctid, resultset, resultType),
            ownership: () => api.ownURL(clientid, newMarkup, urlid, possiblyAffectedUrlIds, ts, start, end, ctid, resultset, resultType),
            authority: () => api.setAuthority(clientid, newMarkup, urlid, start, end, ctid, resultset, resultType),
        }[markupType];

        return callApi().then(response => {
            // Prevents errors when trying to update the UI of the markupEditor after the markupEditor modal is closed (i.e. markupEditorData = null) or when a user opens a new modal before the previous response arrives.
            // Ensures we only update the UI of the markupEditor if the markupEditor modal is open and the currently opened markupEditor's URL ID exists in the response.
            if (getters.markupEditorData?.urlid in response.data) {
                commit('UPDATE_MARKUP_EDITOR_DATA', { markup: response.data[getters.markupEditorData.urlid] });
            }

            commit('BUMP_CHARTS_CACHE_VERSION');

            return response.data;
        });
    },
    // DM-10271: mark one url from outside the Markup dialog (a PAA source chip): the same save and
    // timeline repaint as Timeline.onMarkupChangeRequested. target = { url, urlid, ts, resultType }.
    setUrlMarkup({ dispatch, rootGetters }, { target, markupType, newMarkup, ratingAction }) {
        const possiblyAffectedUrlIds = MarkupUtil.getPossiblyAffectedUrlIds(rootGetters['timeline/cards'], target);
        return dispatch('updateMarkup', { markupType, newMarkup, ratingAction, possiblyAffectedUrlIds, target })
            .then((markupByUrlId) => dispatch(
                'timeline/updateEachCard', (card) => MarkupUtil.updateTimelineMarkup(card, markupByUrlId), { root: true }
            ));
    },
    // DM-10271: the client's rating of a People-Also-Ask question. It applies on every date and term,
    // so every card's question with the same text takes it.
    rateQuestion({ dispatch, rootGetters }, { question, score }) {
        const clientid = rootGetters['routeParams/client'].clientid;
        return api.rateQuestion(clientid, question, score).then(() => {
            dispatch('timeline/updateEachCard', (card) => {
                card.result
                    .filter((result) => result.resulttype === 'questionanswerbox')
                    .flatMap((result) => result.question || [])
                    .filter((q) => q.title === question)
                    .forEach((q) => { q.questionMarkup = { ...q.questionMarkup, rating: score }; });
            }, { root: true });
        });
    },
    async addAiAnalysisPollUrls({ commit, state, dispatch, rootGetters }, urls) {

        if (!urls?.length) {
            console.debug("not initiating polling - no urls");
            return;
        }

        if (isSameClientAsAiPolling(state, rootGetters)) {
            // if the client changed during a previous polling, cancel the old poller before proceeding. Note that the poller will also clear it if it tries to update markup and the client changed.
            console.debug("cancelling previous polling - client changed");
            dispatch('cancelAiAnalysisPolling');
        }

        let allUrls = Array.from(new Set(state.aiAnalysisPollUrls.concat(urls)));
        commit('SET_AI_ANALYSIS_POLL_URLS', allUrls);

        if (!state.aiAnalysisPollTimerId) {
            let timerId = setInterval(() => aiAnalysisPoller(state, dispatch, rootGetters), 5000);
            commit('SET_AI_ANALYSIS_POLL_TIMER_ID', timerId);
            commit('SET_AI_ANALYSIS_POLL_CLIENT_ID', rootGetters['routeParams/client'].clientid);
        }
    },

    removeAiAnalysisPollUrls({ commit, state }, urls) {
        const allUrls = state.aiAnalysisPollUrls.filter(item => !urls.includes(item));
        commit('SET_AI_ANALYSIS_POLL_URLS', allUrls);

        if (allUrls.length === 0) {
            clearInterval(state.aiAnalysisPollTimerId);
            commit('SET_AI_ANALYSIS_POLL_TIMER_ID', null);
        }
    },

    cancelAiAnalysisPolling({ commit, state }) {
        if (state.aiAnalysisPollTimerId) {
            clearInterval(state.aiAnalysisPollTimerId);
            commit('SET_AI_ANALYSIS_POLL_TIMER_ID', null);
            commit('SET_AI_ANALYSIS_POLL_URLS', []);
            commit('SET_AI_ANALYSIS_POLL_CLIENT_ID', null);
        }
    },
};


const mutations = {
    SET_RATING(state, rating) {
        state.rating = rating;
        storage.set("markupRating", JSON.stringify(rating.map(option => option.value)), true);
        legacyStoreHighlight(state);
    },
    SET_OWNERSHIP(state, ownership) {
        state.ownership = ownership;
        storage.set("markupOwnership", JSON.stringify(ownership.map(option => option.value)), true);
        legacyStoreHighlight(state);
    },
    SET_AUTHORITY(state, authority) {
        state.authority = authority;
        storage.set("markupAuthority", JSON.stringify(authority.map(option => option.value)), true);
        legacyStoreHighlight(state);
    },

    SET_MARKUP(state, markup) {
        state.markup = markup;
        storage.set("hlview", markup || "none", true);
        legacyStoreHighlight(state);
    },
    SET_HIGHLIGHT_MARKUP(state, highlightMarkup) {
        state.highlightMarkup = highlightMarkup;
        storage.set("highlightMarkup", String(highlightMarkup), true);
        legacyStoreHighlight(state);
    },

    SET_MARKUP_EDITOR_DATA(state, payload) {
        state.markupEditorData = payload;
    },
    SET_MARKUP_RESULT_DATA(state, payload) {
        state.markupResultData = payload;
    },
    UPDATE_MARKUP_EDITOR_DATA(state, payload) {
        Object.assign(state.markupEditorData, payload);
    },

    SET_SHOW_AI_MARKUP(state, payload) {
        state.showAiMarkup = payload;
        storage.set("showAiMarkup", payload, true);
    },

    SET_AI_ANALYSIS_POLL_URLS(state, payload) {
        state.aiAnalysisPollUrls = payload;
    },

    SET_AI_ANALYSIS_POLL_TIMER_ID(state, payload) {
        state.aiAnalysisPollTimerId = payload;
    },

    SET_AI_ANALYSIS_POLL_CLIENT_ID(state, payload) {
        state.aiAnalysisPollClientId = payload;
    },

    BUMP_CHARTS_CACHE_VERSION(state) {
        state.chartsCacheVersion++;
    },
};

function isSameClientAsAiPolling(state, rootGetters) {
    const clientid = rootGetters['routeParams/client'].clientid;
    return state.aiAnalysisPollClientId && clientid === state.aiAnalysisPollClientId;
}

function isAiPollingRelevant(state, rootGetters) {
    const cards = rootGetters['timeline/cards'];
    const isNews = rootGetters['routeParams/view'] === "timelineNews";
    return cards?.length && isSameClientAsAiPolling(state, rootGetters) && isNews;
}

async function aiAnalysisPoller(state, dispatch, rootGetters) {
    console.debug("polling");

    // if the timeline is not relevant for the registered poll because it changed during polling, skip this poll.
    // check before actually polling the api to avoid unnecessary api calls, and check again after polling to make sure it didn't change in the meantime
    // Don't cancel the timer because the user may go back to it
    // If this happens, thought about setting a limit on the number of polls to avoid infinite polling, but it seems unnecessary because the poller isn't doing much
    if (!isAiPollingRelevant(state, rootGetters)) {
        console.debug("skipping poll - not relevant to current view");
        return;
    }

    const clientid = rootGetters['routeParams/client'].clientid;
    try {
        const apiResponse = await api.getAnalysisData(clientid, state.aiAnalysisPollUrls, "news");
        if (!isAiPollingRelevant(state, rootGetters)) {
            console.debug("ignoring poll - not relevant to current view");
            return;
        }
        // nothing finished yet
        if (!apiResponse.data?.length) {
            console.debug("ignoring poll - no urls finished yet");
            return;
        }

        // update the timeline
        // Include analysisKeyword from request so UI can show if analysis was run for different keyword
        const analysisMap = new Map(apiResponse.data.map(item => [
            item.request.urlInfo.urlid,
            { ...item.response, analysisKeyword: item.request.keyword || "" }
        ]));
        console.debug({ poller: "poller", analysisMap });
        dispatch('timeline/updateEachCard', (card) => {
            for (const result of card.result) {
                const detailSections = result.containerDetailSections || (result.detailSection ? [result.detailSection] : []);
                detailSections.forEach((section) => {
                    // it's possible the user changed the query and now the result has the markup. Shouldn't hurt to set it anyway but let's minimize risk
                    if (analysisMap.has(section.urlid) && !section.aimarkup?.ratingReason) {
                        section.aimarkup = analysisMap.get(section.urlid);
                    }
                });
            }
        }, { root: true });

        // Handle if the result is open while updating timeline
        if (analysisMap.has(state.markupEditorData?.urlid) && !state.markupEditorData?.aimarkup) {
            dispatch('setMarkupEditorData', { aimarkup: analysisMap.get(state.markupEditorData.urlid) });
        }


        dispatch('removeAiAnalysisPollUrls', apiResponse.data.map(item => item.request.urlInfo.urlid));


    } catch (error) {
        console.error("Error during AI analysis polling:", error);
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
